import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { CHART_COLORS, getTheme } from './chartTheme'
import ChartTooltip from './ChartTooltip'

const CashflowChart = ({ data = [], height = 300, isDark = false }) => {
  const { t } = useTranslation('common')
  const theme = getTheme(isDark)

  const chartData = data.map((item) => ({
    name: item.label || item.month || item.day,
    dochody: parseFloat(item.income || 0),
    wydatki: parseFloat(item.expenses || item.total || 0),
  }))

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height, color: 'var(--color-text-faint)' }}>
        {t('noData')}
      </div>
    )
  }

  const formatCurrency = (val) => {
    if (val >= 1000) return `${(val / 1000).toFixed(1)}k ${t('currency')}`
    return `${val} ${t('currency')}`
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="cashflowIncome" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={CHART_COLORS.success} stopOpacity={0.25} />
            <stop offset="100%" stopColor={CHART_COLORS.success} stopOpacity={0.0} />
          </linearGradient>
          <linearGradient id="cashflowExpense" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={CHART_COLORS.primary} stopOpacity={0.2} />
            <stop offset="100%" stopColor={CHART_COLORS.primary} stopOpacity={0.0} />
          </linearGradient>
        </defs>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={theme.grid}
          vertical={false}
        />
        <XAxis
          dataKey="name"
          axisLine={false}
          tickLine={false}
          tick={{ fill: theme.textSecondary, fontSize: 12 }}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fill: theme.textSecondary, fontSize: 12 }}
          tickFormatter={formatCurrency}
          width={65}
        />
        <Tooltip
          content={
            <ChartTooltip
              formatter={(value, name) => {
                const label = name === 'dochody' ? t('dashboard:income') : t('dashboard:expenses')
                return `${label}: ${parseFloat(value).toFixed(2)} ${t('currency')}`
              }}
            />
          }
        />
        <Area
          type="monotone"
          dataKey="dochody"
          stroke={CHART_COLORS.success}
          strokeWidth={2.5}
          fill="url(#cashflowIncome)"
          dot={{ r: 4, fill: CHART_COLORS.success, stroke: theme.dotStroke, strokeWidth: 2 }}
          activeDot={{ r: 6, fill: CHART_COLORS.success, stroke: theme.dotStroke, strokeWidth: 2 }}
        />
        <Area
          type="monotone"
          dataKey="wydatki"
          stroke={CHART_COLORS.primary}
          strokeWidth={2.5}
          fill="url(#cashflowExpense)"
          dot={{ r: 4, fill: CHART_COLORS.primary, stroke: theme.dotStroke, strokeWidth: 2 }}
          activeDot={{ r: 6, fill: CHART_COLORS.primary, stroke: theme.dotStroke, strokeWidth: 2 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

export default CashflowChart
